import { useAtomValue } from "jotai";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAutorenew,
  MdDvr,
  MdExitToApp,
  MdHistory,
  MdOutlineGroups,
  MdOutlinePayments,
  MdOutlineSettings
} from "react-icons/md";
import { TbBrandAppgallery, TbHome, TbReceipt } from "react-icons/tb";

import { TransitEnterExitIcon } from "@/icons";
import {
  useGetProfileDataQuery,
  useLogout,
  type UserMenu
} from "@/modules/auth";
import { userDataAtom } from "@/modules/auth/atoms/auth-atom";
import {
  useGetClaimHistoryBadgeCountQuery,
  useGetClaimOrderBadgeCountQuery
} from "@/modules/order";

import { type MenuItem } from "./components/sidebar";

const SIDEBAR_BADGE_COUNT_LINKS = {
  externalPayment: "/disbursement",
  claimOrders: "/disbursement/claim-orders",
  claimHistory: "/disbursement/claim-history"
} as const;

type SidebarBadgeCountByLink = Partial<
  Record<string, number | null | undefined>
>;

const getTotalBadgeCount = (counts: (number | null | undefined)[]) => {
  return counts.reduce<number>((total, count) => total + (count ?? 0), 0);
};

const withBadgeCounts = (
  menuItems: (MenuItem | null)[],
  badgeCountByLink: SidebarBadgeCountByLink
): (MenuItem | null)[] => {
  return menuItems.map((item) => {
    if (!item) return null;

    return {
      ...item,
      badgeCount: item.link
        ? (badgeCountByLink[item.link] ?? item.badgeCount)
        : item.badgeCount,
      children: item.children?.map((child) => ({
        ...child,
        badgeCount: badgeCountByLink[child.link] ?? child.badgeCount
      }))
    };
  });
};

export const useMenuItems = () => {
  const logout = useLogout();
  const { t } = useTranslation("common");

  const { data, isLoading } = useGetProfileDataQuery();
  const userData = useAtomValue(userDataAtom);
  const userRoles = userData?.roles || [];
  const isSuperAdmin = userRoles.includes("superadmin");
  const hasDisbursementMenu = useMemo(
    () =>
      data?.data?.menus?.some((menu) => menu.path === "/disbursement") ?? false,
    [data?.data?.menus]
  );

  const {
    data: claimOrderBadgeCountData,
    isSuccess: isClaimOrderBadgeCountSuccess
  } = useGetClaimOrderBadgeCountQuery({
    options: {
      enabled: hasDisbursementMenu,
      staleTime: 60 * 1000,
      retry: false,
      meta: {
        errorMessage: ""
      }
    }
  });
  const {
    data: claimHistoryBadgeCountData,
    isSuccess: isClaimHistoryBadgeCountSuccess
  } = useGetClaimHistoryBadgeCountQuery({
    options: {
      enabled: hasDisbursementMenu,
      staleTime: 60 * 1000,
      retry: false,
      meta: {
        errorMessage: ""
      }
    }
  });

  const claimOrderBadgeCount = claimOrderBadgeCountData?.data?.count;
  const claimHistoryBadgeCount = claimHistoryBadgeCountData?.data?.count;

  const externalPaymentBadgeCount = useMemo(() => {
    if (!isClaimOrderBadgeCountSuccess || !isClaimHistoryBadgeCountSuccess) {
      return undefined;
    }

    return getTotalBadgeCount([claimOrderBadgeCount, claimHistoryBadgeCount]);
  }, [
    claimHistoryBadgeCount,
    claimOrderBadgeCount,
    isClaimHistoryBadgeCountSuccess,
    isClaimOrderBadgeCountSuccess
  ]);

  const sidebarBadgeCountByLink = useMemo<SidebarBadgeCountByLink>(
    () => ({
      [SIDEBAR_BADGE_COUNT_LINKS.externalPayment]: externalPaymentBadgeCount,
      [SIDEBAR_BADGE_COUNT_LINKS.claimOrders]: claimOrderBadgeCount,
      [SIDEBAR_BADGE_COUNT_LINKS.claimHistory]: claimHistoryBadgeCount
    }),
    [claimHistoryBadgeCount, claimOrderBadgeCount, externalPaymentBadgeCount]
  );

  const constructMenuItems = useCallback(
    (menus: UserMenu[]): (MenuItem | null)[] => {
      return menus.map((menu) => {
        switch (menu.path) {
          case "/":
            return {
              link: menu.path,
              label: menu.name,
              icon: <TbHome size={20} />
            };

          case "/products":
            return {
              link: menu.path,
              label: t("menuItems.product.label"),
              icon: <TbBrandAppgallery size={20} />,
              children: [
                {
                  link: "/products/list",
                  label: t("menuItems.product.items.myProducts.label")
                },

                ...(isSuperAdmin
                  ? [
                      {
                        link: "/products/integration",
                        label: t("menuItems.product.items.integration.label")
                      },
                      {
                        link: "/products/automation",
                        label: t("menuItems.product.items.automation.label")
                      }
                    ]
                  : [])
              ]
            };

          case "/vouchers":
            return {
              link: menu.path,
              label: menu.name,
              icon: <TbReceipt size={20} />
            };

          case "/orders":
            return {
              link: menu.path,
              label: menu.name,
              icon: <TransitEnterExitIcon size={20} />
            };

          case "/transaction-history":
            return {
              link: menu.path,
              label: menu.name,
              icon: <MdHistory size={20} />
            };

          case "/users":
            return {
              link: menu.path,
              label: menu.name,
              icon: <MdOutlineGroups size={20} />,
              children: [
                {
                  link: "/users/buyers",
                  label: t("menuItems.userManagement.items.buyer.label")
                },
                {
                  link: "/users/sellers",
                  label: t("menuItems.userManagement.items.seller.label")
                },
                {
                  link: "/users/sales",
                  label: t("menuItems.userManagement.items.sales.label")
                }
              ]
            };

          case "/disbursement":
            return {
              link: menu.path,
              label: menu.name,
              icon: <MdOutlinePayments size={20} />,
              children: [
                {
                  link: "/disbursement/claim-orders",
                  label: t("menuItems.externalPayment.items.claimOrders.label")
                },
                {
                  link: "/disbursement/claim-history",
                  label: t("menuItems.externalPayment.items.claimList.label")
                }
              ]
            };

          case "/recurring-orders":
            return {
              link: menu.path,
              label: menu.name,
              icon: <MdAutorenew size={20} />
            };
          case "/billing":
            return {
              link: menu.path,
              label: menu.name,
              icon: <MdDvr size={20} />,
              children: [
                {
                  link: "/billing/report",
                  label: t("menuItems.billing.items.billingReports.label")
                },
                {
                  link: "/billing/tax",
                  label: t("menuItems.billing.items.billingTax.label")
                }
              ]
            };

          case "/settings":
            return {
              link: menu.path,
              label: menu.name,
              icon: <MdOutlineSettings size={20} />,
              children: [
                {
                  link: "/settings/profile",
                  label: t("menuItems.settings.items.profile.label")
                },
                {
                  link: "/settings/bank-information",
                  label: t("menuItems.settings.items.bankInformation.label")
                },
                {
                  link: "/settings/change-password",
                  label: t("menuItems.settings.items.changePassword.label")
                }
              ]
            };

          case "/logout":
            return {
              key: menu.path,
              label: menu.name,
              icon: <MdExitToApp size={20} />,
              onClick: () => logout()
            };

          default:
            return null;
        }
      });
    },
    [logout, t, isSuperAdmin]
  );

  const MENU_ITEMS: (MenuItem | null)[] = useMemo(() => {
    if (!isLoading && data?.data?.menus) {
      return withBadgeCounts(
        constructMenuItems(data?.data?.menus),
        sidebarBadgeCountByLink
      );
    }

    return [];
  }, [
    data?.data?.menus,
    constructMenuItems,
    isLoading,
    sidebarBadgeCountByLink
  ]);

  return { MENU_ITEMS, isLoading };
};
